// ============================================================
// LinuxSecurity.jsx
//
// A branded, responsive course content page for the
// "Linux Security Essentials" lesson.
//
// Author: ProDev Linux Series
// Features: Privilege management, permission hardening,
//           security toolkits, and update protocols.
// ============================================================

import React, { useState, useEffect } from "react";

// ── MUI Layout & Display ──────────────────────────────────
import {
    Box,
    Container,
    Typography,
    Grid,
    Chip,
    Divider,
    Tooltip,
    Collapse,
    Paper,
    Fab,
    Zoom,
    Stack
} from "@mui/material";

// ── MUI Icons ────────────────────────────────────────────
import ShieldIcon             from "@mui/icons-material/Shield";
import LockIcon               from "@mui/icons-material/Lock";
import AdminPanelSettingsIcon from "@mui/icons-material/AdminPanelSettings";
import SystemUpdateAltIcon    from "@mui/icons-material/SystemUpdateAlt";
import ConstructionIcon       from "@mui/icons-material/Construction";
import TerminalIcon           from "@mui/icons-material/Terminal";
import LightbulbIcon          from "@mui/icons-material/Lightbulb";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import LinkIcon               from "@mui/icons-material/Link";
import ExpandMoreIcon         from "@mui/icons-material/ExpandMore";
import ExpandLessIcon         from "@mui/icons-material/ExpandLess";
import ContentCopyIcon        from "@mui/icons-material/ContentCopy";
import DoneIcon               from "@mui/icons-material/Done";
import SchoolIcon             from "@mui/icons-material/School";
import StarIcon               from "@mui/icons-material/Star";
import KeyboardArrowUpIcon    from "@mui/icons-material/KeyboardArrowUp";
import PolicyIcon             from "@mui/icons-material/Policy";

// ── Brand tokens ──────────────────────────────────────────
const BRAND = {
    bg:          "#233D4D",
    surface:     "rgba(255,255,255,0.04)",
    border:      "rgba(218,165,32,0.25)",
    borderHover: "rgba(218,165,32,0.6)",
    gold:        "#DAA520",
    goldBright:  "#FFD700",
    goldGlow:    "rgba(218,165,32,0.15)",
    white:       "#FFFFFF",
    muted:       "rgba(255,255,255,0.55)",
    codeBg:      "rgba(0,0,0,0.45)",
    codeText:    "#FFD700",
};

const sectionHeading = {
    fontFamily: "'Georgia', serif",
    fontWeight: 800,
    color: BRAND.white,
    letterSpacing: "-0.5px",
};

// ── Sub-Components ────────────────────────────────────────

function SectionTitle({ icon, label }) {
    return (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 2.5 }}>
            <Box sx={{
                width: 38, height: 38, borderRadius: "50%",
                background: BRAND.goldGlow, border: `1px solid ${BRAND.border}`,
                display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
            }}>
                {React.cloneElement(icon, { sx: { color: BRAND.gold, fontSize: 20 } })}
            </Box>
            <Typography variant="h6" sx={{ ...sectionHeading, fontSize: { xs: 17, md: 19 } }}>
                {label}
            </Typography>
        </Box>
    );
}

function ConceptCard({ icon, title, description }) {
    return (
        <Paper elevation={0} sx={{
            p: 2.5, height: "100%", background: BRAND.surface,
            border: `1px solid ${BRAND.border}`, borderRadius: 3,
            transition: "0.25s", "&:hover": { borderColor: BRAND.borderHover, boxShadow: `0 0 20px ${BRAND.goldGlow}` },
        }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.2, mb: 1.2 }}>
                {React.cloneElement(icon, { sx: { color: BRAND.gold, fontSize: 22 } })}
                <Typography sx={{ fontFamily: "'Georgia', serif", fontWeight: 700, color: BRAND.white, fontSize: 14 }}>
                    {title}
                </Typography>
            </Box>
            <Typography sx={{ fontSize: 13, color: BRAND.muted, lineHeight: 1.65 }}>
                {description}
            </Typography>
        </Paper>
    );
}

function CodeBlock({ lines }) {
    const [copied, setCopied] = useState(false);
    const fullText = lines.map((l) => l.cmd).join("\n");

    const handleCopy = () => {
        navigator.clipboard.writeText(fullText).then(() => {
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        });
    };

    return (
        <Box sx={{ background: BRAND.codeBg, border: `1px solid ${BRAND.border}`, borderRadius: 2, overflow: "hidden", mb: 2 }}>
            <Box sx={{ px: 2, py: 1, display: "flex", alignItems: "center", justifyContent: "space-between", borderBottom: `1px solid ${BRAND.border}`, background: "rgba(0,0,0,0.3)" }}>
                <Box sx={{ display: "flex", gap: 0.7 }}>
                    {["#ff5f56", "#ffbd2e", "#27c93f"].map((c) => (
                        <Box key={c} sx={{ width: 10, height: 10, borderRadius: "50%", background: c }} />
                    ))}
                </Box>
                <Tooltip title={copied ? "Copied!" : "Copy commands"} placement="top">
                    <Box onClick={handleCopy} sx={{ cursor: "pointer", display: "flex", alignItems: "center", gap: 0.5, color: copied ? BRAND.goldBright : BRAND.muted, fontSize: 12 }}>
                        {copied ? <DoneIcon sx={{ fontSize: 15 }} /> : <ContentCopyIcon sx={{ fontSize: 15 }} />}
                        <span style={{ fontFamily: "monospace" }}>{copied ? "Copied" : "Copy"}</span>
                    </Box>
                </Tooltip>
            </Box>
            <Box sx={{ p: 2.5, display: "flex", flexDirection: "column", gap: 1.5 }}>
                {lines.map(({ cmd, comment }, i) => (
                    <Box key={i}>
                        {comment && <Typography sx={{ fontFamily: "monospace", fontSize: 12, color: "rgba(255,255,255,0.3)", mb: 0.3 }}># {comment}</Typography>}
                        <Typography sx={{ fontFamily: "monospace", fontSize: { xs: 13, md: 14 }, color: BRAND.codeText }}>{cmd}</Typography>
                    </Box>
                ))}
            </Box>
        </Box>
    );
}

function ExpandableExample({ title, icon, children }) {
    const [open, setOpen] = useState(false);
    return (
        <Box sx={{ border: `1px solid ${open ? BRAND.borderHover : BRAND.border}`, borderRadius: 2.5, overflow: "hidden", mb: 1.5 }}>
            <Box onClick={() => setOpen(!open)} sx={{ px: 2.5, py: 1.8, display: "flex", alignItems: "center", justifyContent: "space-between", cursor: "pointer", background: open ? BRAND.goldGlow : BRAND.surface }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.2 }}>
                    {React.cloneElement(icon, { sx: { color: BRAND.gold, fontSize: 20 } })}
                    <Typography sx={{ color: BRAND.white, fontWeight: 600, fontSize: 14 }}>{title}</Typography>
                </Box>
                {open ? <ExpandLessIcon sx={{ color: BRAND.gold }} /> : <ExpandMoreIcon sx={{ color: BRAND.muted }} />}
            </Box>
            <Collapse in={open}>
                <Box sx={{ px: 2.5, py: 2, background: "rgba(0,0,0,0.25)", borderTop: `1px solid ${BRAND.border}` }}>{children}</Box>
            </Collapse>
        </Box>
    );
}

function ResourceLink({ href, label, sub }) {
    return (
        <Box component="a" href={href} target="_blank" rel="noreferrer" sx={{
            display: "flex", alignItems: "center", gap: 2, p: 2, mb: 1.2, textDecoration: "none",
            border: `1px solid ${BRAND.border}`, borderRadius: 2, background: BRAND.surface, transition: "0.25s",
            "&:hover": { borderColor: BRAND.borderHover, boxShadow: `0 0 16px ${BRAND.goldGlow}` },
        }}>
            <LinkIcon sx={{ color: BRAND.gold, fontSize: 20, flexShrink: 0 }} />
            <Box><Typography sx={{ color: BRAND.gold, fontWeight: 700, fontSize: 14 }}>{label}</Typography>
                {sub && <Typography sx={{ color: BRAND.muted, fontSize: 12 }}>{sub}</Typography>}</Box>
        </Box>
    );
}

// ── Main Component ────────────────────────────────────────

export default function LinuxSecurity() {
    const [showScroll, setShowScroll] = useState(false);

    useEffect(() => {
        const checkScroll = () => {
            if (!showScroll && window.pageYOffset > 400) setShowScroll(true);
            else if (showScroll && window.pageYOffset <= 400) setShowScroll(false);
        };
        window.addEventListener("scroll", checkScroll);
        return () => window.removeEventListener("scroll", checkScroll);
    }, [showScroll]);

    return (
        <Box sx={{ minHeight: "100vh", background: BRAND.bg, py: { xs: 4, md: 7 }, px: { xs: 2, sm: 3 },
            backgroundImage: `repeating-linear-gradient(-45deg, rgba(218,165,32,0.02) 0px, rgba(218,165,32,0.02) 1px, transparent 1px, transparent 12px)`
        }}>
            <Container maxWidth="lg">
                {/* HERO HEADER */}
                <Box sx={{ mb: { xs: 5, md: 7 }, textAlign: { xs: "center", md: "left" } }}>
                    <Chip icon={<SchoolIcon sx={{ color: `${BRAND.gold} !important`, fontSize: 15 }} />} label="Security · Module 04"
                          sx={{ mb: 2.5, background: BRAND.goldGlow, border: `1px solid ${BRAND.border}`, color: BRAND.gold, fontFamily: "monospace", fontSize: 11 }} />
                    <Typography variant="h3" sx={{ ...sectionHeading, fontSize: { xs: 28, sm: 36, md: 44 }, mb: 2 }}>
                        Linux <span>Security Essentials</span>
                    </Typography>
                    <Typography sx={{ color: BRAND.muted, fontSize: { xs: 14, md: 16 }, maxWidth: 620, lineHeight: 1.75, mx: { xs: "auto", md: 0 } }}>
                        Linux is secure by design, but true protection comes from proactive configuration. Hardening your system involves managing privileges, monitoring logs, and shielding your network.
                    </Typography>
                </Box>

                {/* OVERVIEW */}
                <Box sx={{ mb: 6 }}>
                    <SectionTitle icon={<ShieldIcon />} label="Defense in Depth" />
                    <Typography sx={{ color: BRAND.muted, lineHeight: 1.8, fontSize: { xs: 13, md: 15 } }}>
                        A secure Linux environment relies on multiple layers of protection. At <strong style={{ color: BRAND.white }}>ProDevConsult</strong>, we follow the principle of least privilege: users and applications should only have the exact permissions they need to function—and nothing more.
                    </Typography>
                </Box>

                <Divider sx={{ borderColor: BRAND.border, mb: 6 }} />

                {/* KEY CONCEPTS */}
                <Box sx={{ mb: 6 }}>
                    <SectionTitle icon={<PolicyIcon />} label="Hardening Pillars" />
                    <Grid container spacing={2}>
                        {[
                            { icon: <AdminPanelSettingsIcon />, title: "Privilege Control", description: "Using sudo and visudo to grant administrative power safely without sharing the root password." },
                            { icon: <LockIcon />, title: "Permission Masking", description: "Applying strict chmod and chown rules to sensitive files like /etc/shadow and SSH keys." },
                            { icon: <SystemUpdateAltIcon />, title: "Patch Management", description: "Regularly updating the kernel and packages to eliminate known security vulnerabilities (CVEs)." },
                        ].map((card) => (
                            <Grid item xs={12} md={4} key={card.title}><ConceptCard {...card} /></Grid>
                        ))}
                    </Grid>
                </Box>

                {/* TRY IT YOURSELF */}
                <Box sx={{ mb: 6 }}>
                    <SectionTitle icon={<TerminalIcon />} label="Security Administration" />

                    <Typography sx={{ color: BRAND.white, fontWeight: 700, fontSize: 14, mb: 1.2 }}>🔐 Managing sudo Privileges</Typography>
                    <CodeBlock lines={[
                        { cmd: "$ sudo usermod -aG sudo hazzan", comment: "Grant user 'hazzan' admin rights (Ubuntu/Debian)" },
                        { cmd: "$ sudo visudo",                comment: "Safely edit the sudoers file with syntax checking" },
                        { cmd: "$ sudo -l",                   comment: "List the specific commands you are allowed to run" },
                    ]} />

                    <Typography sx={{ color: BRAND.white, fontWeight: 700, fontSize: 14, mb: 1.2, mt: 3 }}>🧱 Permission Hardening</Typography>
                    <CodeBlock lines={[
                        { cmd: "$ chmod 700 ~/.ssh",         comment: "Restrict SSH folder to owner-only access" },
                        { cmd: "$ chmod 600 ~/.ssh/id_rsa",   comment: "Secure private keys (read/write for owner only)" },
                        { cmd: "$ sudo chown root:root /etc/shadow", comment: "Ensure password file is root-owned" },
                    ]} />

                    <Typography sx={{ color: BRAND.white, fontWeight: 700, fontSize: 14, mb: 2, mt: 4 }}>🔽 Modern Security Toolset</Typography>

                    <ExpandableExample title="System Auditing & Scanning" icon={<ConstructionIcon />}>
                        <Grid container spacing={2}>
                            {[
                                { tool: "ufw", desc: "Uncomplicated Firewall. Easily allow/deny network ports." },
                                { tool: "fail2ban", desc: "IPS that bans IPs showing malicious signs (failed logins)." },
                                { tool: "lynis", desc: "Security auditing tool for system hardening tests." },
                                { tool: "chkrootkit", desc: "Locally checks for signs of a rootkit infection." },
                            ].map(item => (
                                <Grid item xs={12} sm={6} key={item.tool}>
                                    <Typography sx={{ color: BRAND.goldBright, fontFamily: "monospace", fontSize: 13 }}>{item.tool}</Typography>
                                    <Typography sx={{ color: BRAND.muted, fontSize: 12 }}>{item.desc}</Typography>
                                </Grid>
                            ))}
                        </Grid>
                    </ExpandableExample>

                    <ExpandableExample title="Updating Across Distributions" icon={<SystemUpdateAltIcon />}>
                        <CodeBlock lines={[
                            { cmd: "$ sudo apt update && sudo apt upgrade -y", comment: "Debian / Ubuntu / Kali" },
                            { cmd: "$ sudo dnf update",                       comment: "Fedora / RHEL / CentOS" },
                            { cmd: "$ sudo pacman -Syu",                      comment: "Arch Linux" },
                        ]} />
                    </ExpandableExample>

                    <ExpandableExample title="Critical Audit Logs" icon={<LightbulbIcon />}>
                        <Typography sx={{ color: BRAND.muted, fontSize: 13, mb: 2 }}>Monitor these files to detect intrusion attempts:</Typography>
                        <Grid container spacing={1}>
                            {[
                                "/var/log/auth.log (Auth attempts)",
                                "/var/log/syslog (General system events)",
                                "/var/log/fail2ban.log (Banned IPs)",
                            ].map(log => (
                                <Grid item xs={12} key={log}><Typography sx={{ color: BRAND.codeText, fontFamily: "monospace", fontSize: 12 }}>• {log}</Typography></Grid>
                            ))}
                        </Grid>
                    </ExpandableExample>
                </Box>

                <Divider sx={{ borderColor: BRAND.border, mb: 6 }} />

                {/* OUTCOMES */}
                <Box sx={{ mb: 6 }}>
                    <SectionTitle icon={<StarIcon />} label="Security Outcomes" />
                    <Grid container spacing={1.5}>
                        {[
                            "Implement the Principle of Least Privilege",
                            "Manage sudoers access without root exposure",
                            "Secure sensitive directories using octal permissions",
                            "Deploy basic firewall rules and brute-force protection",
                            "Audit system health using Lynx and log inspection",
                            "Maintain a CVE-free environment through updates",
                        ].map((item) => (
                            <Grid item xs={12} sm={6} key={item}>
                                <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.2 }}>
                                    <CheckCircleOutlineIcon sx={{ color: BRAND.gold, fontSize: 19, mt: 0.1 }} />
                                    <Typography sx={{ color: BRAND.muted, fontSize: 13 }}>{item}</Typography>
                                </Box>
                            </Grid>
                        ))}
                    </Grid>
                </Box>

                {/* RESOURCES */}
                <Box sx={{ mb: 4 }}>
                    <SectionTitle icon={<LinkIcon />} label="Technical Deep Dive" />
                    <ResourceLink href="https://linuxsecurity.expert/" label="LinuxSecurity.Expert" sub="Curated security news, tutorials, and vulnerability feeds." />
                    <ResourceLink href="https://wiki.archlinux.org/title/Security" label="Arch Wiki Security" sub="A masterclass on hardening any Linux distribution." />
                    <ResourceLink href="https://linuxize.com/post/how-to-secure-your-linux-server/" label="Hardening Guide - Linuxize" sub="Practical steps for securing production cloud servers." />
                </Box>

                <Box sx={{ mt: 2, p: 2.5, border: `1px solid ${BRAND.border}`, borderRadius: 2, background: BRAND.goldGlow, display: "flex", alignItems: "center", gap: 1.5 }}>
                    <LightbulbIcon sx={{ color: BRAND.gold }} />
                    <Typography sx={{ color: BRAND.muted, fontSize: 13 }}>
                        <strong style={{ color: BRAND.white }}>Pro Tip:</strong> Never log in as <strong>root</strong> directly over SSH. Always log in as a standard user and use <code style={{ color: BRAND.goldBright }}>sudo</code>. This creates an audit trail in <code style={{ color: BRAND.goldBright }}>/var/log/auth.log</code> showing exactly who performed which administrative action.
                    </Typography>
                </Box>
            </Container>

            {/* ✅ Floating Scroll-to-Top */}
            <Stack spacing={2} sx={{ position: "fixed", bottom: 25, right: 25, zIndex: 1000 }}>
                <Zoom in={showScroll}>
                    <Fab size="small" onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })} sx={{ bgcolor: BRAND.gold, "&:hover": { bgcolor: BRAND.goldBright } }}>
                        <KeyboardArrowUpIcon sx={{ color: "#100720" }} />
                    </Fab>
                </Zoom>
            </Stack>
        </Box>
    );
}
